import { DragEndEvent } from "@dnd-kit/core";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDirection } from "../../../hooks/use-direction";
import { FilterOptions } from "../../../hooks/useFilters";
import { useTanStackTable } from "../../../hooks/useTanStackTable";
import { RootState } from "../../../redux/store";
import { getColumnVisibility } from "../../../redux/tableSlice";
import MainTable, { RowColorRules } from "./MainTable";
import TablePagination from "./TablePagination";
import TableToolbar from "./TableToolbar";

type MainTableWaitColumnConfigProps = {
    id: string;
    defaultColumnConfig: any[];
    data: any[];
    filters: FilterOptions;
    floatingHorizontalScrollbar?: boolean;
    multiActions?: any;
    handleMultipleDelete?: Function;
    multiDeletePermission?: { action: string; group: string } | null;
    isLoading?: boolean;
    viewFilters?: boolean;
    pagination?: boolean;
    paginationData?: any;
    search?: boolean;
    defaultOrder?: any;
    updateFilterOrder?: Function;
    updateFilters?: Function;
    toggleFilters?: Function;
    filter?: boolean;
    children?: React.ReactNode;
    largeTable?: boolean;
    columnsNoSortables?: any;
    isSortable?: boolean;
    addicionalButtons?: React.ReactNode;
    classNameTable?: string;
    divClassName?: string;
    headerTableClassName?: string;
    customRowsBackgroundColors?: RowColorRules
    disabledIds?: string[] | null;
    footerRow?: (() => Record<string, any>) | null;
    renderRowDropdown?: (row: any) => Record<string, any> | null;
    showToggleButton?: boolean;
    onDragEnd?: (event: DragEndEvent) => void;
}

const fallbackData: any[] = []

const MainTableWaitColumnConfig: React.FC<MainTableWaitColumnConfigProps> = (
    {
        id,
        defaultColumnConfig,
        data,
        filters,
        floatingHorizontalScrollbar = false,
        multiActions,
        handleMultipleDelete,
        multiDeletePermission = null,
        isLoading,
        viewFilters = true,
        pagination,
        paginationData,
        search,
        defaultOrder,
        updateFilterOrder,
        updateFilters,
        toggleFilters,
        filter = true,
        children,
        largeTable = false,
        columnsNoSortables = [],
        isSortable = false,
        addicionalButtons = null,
        customRowsBackgroundColors = null,
        disabledIds = null,
        footerRow = null,
        renderRowDropdown = null,
        classNameTable = '',
        divClassName = '',
        headerTableClassName = '',
        showToggleButton = true,
        onDragEnd
    }) => {

    const columnVisibility = useSelector((state: any) => {
        const stateData = state.table;
        const columnVisibility = getColumnVisibility(stateData, id);
        return columnVisibility;
    });

    const { direction } = useDirection();
    const { user } = useSelector((state: RootState) => state.auth);

    const { table, setData, tableData, setConlumnConfig } = useTanStackTable<any>({
        tableData: data ?? fallbackData,
        columnConfig: defaultColumnConfig,
        options: {
            /** Stable row identity for server-side data (avoids wrong Switch/selection after refetch when numbers repeat, etc.) */
            getRowId: (originalRow: { id?: string }, index: number) =>
                originalRow?.id != null && originalRow.id !== '' ? String(originalRow.id) : `row-${index}`,
            initialState: {
                pagination: {
                    pageIndex: filters.page <= 1 ? 0 : filters.page - 1,
                    pageSize: filters.limit,
                },
            },
            enableRowSelection: (row) => row.original.id !== user?.id && !disabledIds?.includes(row.original.id),
            meta: { handleMultipleDelete, multiActions, multiDeletePermission },
            enableColumnResizing: true,
            columnResizeDirection: direction as any,
            columnResizeMode: 'onChange',
        },
    });

    useEffect(() => {
        if (Array.isArray(data)) {
            setData(data);
        }
        if (data === undefined || data === null) {
            setData([]);
        }
    }, [data]);

    useEffect(() => {
        setConlumnConfig(defaultColumnConfig);
    }, [defaultColumnConfig]);

    useEffect(() => {
        if (table?.getRowModel().rows.length === 0) return;
        Object.keys(columnVisibility).forEach((key) => {
            const isVisible = columnVisibility[key];
            const column = table.getColumn(key);
            if (column && column != undefined) column.toggleVisibility(isVisible);
        });
    }, [tableData]);

    return (
        <>
            {viewFilters && (
                <TableToolbar tableId={id} table={table} viewFilters={viewFilters} filters={filters} search={search} updateFilters={updateFilters} toggleFilters={toggleFilters} filter={filter} children={children} addicionalButtons={addicionalButtons} showToggleButton={showToggleButton} />
            )
            }
            <MainTable
                table={table}
                isLoading={isLoading}
                variant={'elegant'}
                floatingHorizontalScrollbar={floatingHorizontalScrollbar}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={defaultOrder}
                largeTable={largeTable}
                isSortable={isSortable}
                columnsNoSortables={columnsNoSortables}
                customRowsBackgroundColors={customRowsBackgroundColors}
                footerRow={footerRow}
                renderRowDropdown={renderRowDropdown}
                classNameTable={classNameTable}
                divClassName={divClassName}
                headerTableClassName={headerTableClassName}
                onDragEnd={onDragEnd}
            />
            {pagination && <TablePagination table={table} paginationData={paginationData} />}
        </>
    );
}

export default MainTableWaitColumnConfig;
